import { useRoute, useRouter } from '#imports'
import { type MaybeRefOrGetter, type Ref, onMounted, ref, toValue } from 'vue'

import type { OutletCategorySlug } from '../constants'

const INTRO_PLAYED_KEY_PREFIX = 'outlet-intro-played'

/**
 * Intro splash gate.
 *
 * - For home page (no categorySlug): Shows intro based on `isSessionFirstLaunch=true`
 *   query parameter AND sessionStorage check. The query param is removed immediately
 *   from the URL for cosmetics, but sessionStorage ensures the intro won't re-show
 *   on back navigation (resilient to WebView back-forward list behavior).
 * - For category pages (with categorySlug): Shows intro once per session (browser tab)
 *   using sessionStorage.
 *
 * It's shown only after mount so the server never renders the overlay —
 * avoiding any hydration mismatch.
 *
 * @param categorySlug - Optional category slug to track intro per category.
 *                       If not provided, uses query param logic for home page.
 * @returns Object with:
 *  - shouldShowIntro: Whether to show the intro
 *  - dismiss: Hides the intro and marks it as played in sessionStorage
 *  - reveal: Re-shows the intro (useful for pull-to-reveal gestures)
 */
export function useOutletIntroState(
  categorySlug?: MaybeRefOrGetter<OutletCategorySlug>,
): {
  shouldShowIntro: Ref<boolean>
  dismiss: () => void
  reveal: () => void
} {
  const shouldShowIntro = ref(false)
  const route = useRoute()
  const router = useRouter()

  const getStorageKey = () => {
    const slug = categorySlug ? toValue(categorySlug) : null

    return slug ? `${INTRO_PLAYED_KEY_PREFIX}-${slug}` : INTRO_PLAYED_KEY_PREFIX
  }

  onMounted(() => {
    const slug = categorySlug ? toValue(categorySlug) : null

    if (slug) {
      // Category pages: use sessionStorage (existing behavior)
      const storageKey = getStorageKey()
      const hasPlayedIntro = sessionStorage.getItem(storageKey)
      shouldShowIntro.value = !hasPlayedIntro
    } else {
      // Home page: query param AND sessionStorage check
      // This guards against back navigation in WebView where the back-forward
      // list might return to the original URL with the query param
      const isFirstLaunch = route.query.isSessionFirstLaunch === 'true'
      const storageKey = getStorageKey()
      const hasPlayed = sessionStorage.getItem(storageKey) !== null

      shouldShowIntro.value = isFirstLaunch && !hasPlayed

      // Remove query param from URL for cosmetics
      if (route.query.isSessionFirstLaunch !== undefined) {
        const { isSessionFirstLaunch: _, ...restQuery } = route.query
        router.replace({ query: restQuery })
      }
    }
  })

  function dismiss() {
    shouldShowIntro.value = false

    // Always update sessionStorage (for both home and category pages)
    const storageKey = getStorageKey()
    sessionStorage.setItem(storageKey, 'true')
  }

  function reveal() {
    shouldShowIntro.value = true
  }

  return { shouldShowIntro, dismiss, reveal }
}
